import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {assert} from 'Asserts';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import runModal from 'ui-library/runModal';
import StageMenu from 'workflows/components/StageMenu';
import StageRunMenu from 'workflows/components/StageRunMenu';
import * as WorkflowDetailsTableReduxActions from 'workflows/components/WorkflowDetailsTableReduxActions';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';
import {
  openConfigureStageRunsModalToBulkCreateStageRuns,
  openConfigureStageRunsModalToCreateStageRun,
  openConfigureStageRunsModalToDuplicateBranch,
  openConfigureStageRunsModalToEditStageRun,
} from 'workflows/modals/ConfigureStageRunsModalUtil';
import SetExperimentalConditionsModal from 'workflows/modals/SetExperimentalConditionsModal';
import {WorkflowSelector} from 'workflows/reduceWorkflows';
import * as StageRunActions from 'workflows/StageRunActions';
import {stageConfigurationHasExperimentalConditionValues} from 'workflows/util/ExperimentalConditionUtil';
import * as WorkflowUtil from 'workflows/util/WorkflowUtil';
import {
  STAGE_CONFIGURATION_FULL_SHAPE,
  STAGE_RUN_FULL_SHAPE,
  StageConfiguration,
  StageRun,
  Workflow,
  WORKFLOW_FULL_SHAPE,
  WORKFLOW_STAGE_FULL_SHAPE,
  WorkflowStage,
} from 'workflows/WorkflowConstants';

import {AggregatedStageRunGraph} from './AggregatedNodeGraph';
import {AggregatedStageRunVisualization} from './AggregatedStageRunVisualization';
import {GraphLayout} from './GraphLayout';
import {AGGREGATED_STAGE_RUN_RADIUS, STAGE_RUN_RADIUS} from './GraphLayoutConstants';
import {getLinkHighlightMode, getNodeHighlightMode, HighlightMode} from './StageFlowNodeHighlightUtil';
import StageFlowNodeVisualization, {
  StageFlowNodeSecondaryLinkVisualization,
} from './StageFlowNodeVisualization';
import {StageRunLinkVisualization} from './StageRunVisualization';
import StageVisualization from './StageVisualization';
import {StageRunGraph} from './TaggedNodeGraph';
import * as WorkflowVisualizationReduxActions from './WorkflowVisualizationReduxActions';

function mapStateToProps(
  state: BenchlingState,
  {workflowId}: BaseWorkflowVisualizationProps
): ConnectedProps {
  const workflowSelector = new WorkflowSelector(state.models.workflows);
  const workflow = workflowSelector.getWorkflow(workflowId)!;
  return {
    clickedUpstreamNodeId: state.ui.workflowVisualization.clickedUpstreamNodeId,
    clickedDownstreamNodeId: state.ui.workflowVisualization.clickedDownstreamNodeId,
    rightClickedNodeId: state.ui.workflowVisualization.rightClickedNodeId,
    hoveredNodeId: state.ui.workflowVisualization.hoveredNodeId,
    stageConfigurations: workflowSelector.getStageConfigurations(workflow.workflowTemplateVersion.id)!,
    workflow,
  };
}

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onReset(): void {
      dispatch(WorkflowVisualizationReduxActions.reset());
    },
    onClickNode(nodeId: string): void {
      dispatch(WorkflowVisualizationReduxActions.clickNode(nodeId));
    },
    onClickLink(parentNodeId: string | null, childNodeId: string | null): void {
      dispatch(WorkflowVisualizationReduxActions.clickLink(parentNodeId, childNodeId));
    },
    onRightClickNode(nodeId: string): void {
      dispatch(WorkflowVisualizationReduxActions.rightClickNode(nodeId));
    },
    onClearRightClick(): void {
      dispatch(WorkflowVisualizationReduxActions.rightClickNode(null));
    },
    onHoverNode(nodeId: string): void {
      dispatch(WorkflowVisualizationReduxActions.hoverNode(nodeId));
    },
    onUnhoverNode(nodeId: string): void {
      dispatch(WorkflowVisualizationReduxActions.unhoverNode(nodeId));
    },
    toggleStage(stageId: string): void {
      dispatch(WorkflowDetailsTableReduxActions.toggleStage(stageId));
    },
  };
}

interface BaseWorkflowVisualizationProps {
  containerWidth: number;
  workflowStages: Array<WorkflowStage>;
  stageIdToStageRuns: {[stageId: string]: Array<StageRun>};
  stageIdToStageConfiguration: {[stageId: string]: StageConfiguration};
  stageRunGraph: StageRunGraph;
  workflowId: string;
  isWorkflowEditable: boolean;
}

interface ConnectedProps {
  clickedUpstreamNodeId: string | null;
  clickedDownstreamNodeId: string | null;
  rightClickedNodeId: string | null;
  hoveredNodeId: string | null;
  stageConfigurations: Array<StageConfiguration>;
  workflow: Workflow;
}

interface DispatchProps {
  onReset: () => void;
  onClickNode: (nodeId: string) => void;
  onClickLink: (parentNodeId: string | null, childNodeId: string | null) => void;
  onRightClickNode: (nodeId: string) => void;
  onClearRightClick: () => void;
  onHoverNode: (nodeId: string) => void;
  onUnhoverNode: (nodeId: string) => void;
  toggleStage: (stageId: string) => void;
}

export class WorkflowVisualization extends React.Component<
  BaseWorkflowVisualizationProps & ConnectedProps & DispatchProps
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    // Props to specify when rendering.
    containerWidth: PropTypes.number.isRequired,
    workflowStages: PropTypes.arrayOf(WORKFLOW_STAGE_FULL_SHAPE.isRequired).isRequired,
    stageIdToStageRuns: PropTypes.objectOf(PropTypes.arrayOf(STAGE_RUN_FULL_SHAPE)).isRequired,
    stageIdToStageConfiguration: PropTypes.objectOf(STAGE_CONFIGURATION_FULL_SHAPE).isRequired,
    stageRunGraph: PropTypes.object.isRequired,
    workflowId: PropTypes.string.isRequired,

    // Props provided by connect to redux store
    clickedUpstreamNodeId: PropTypes.string,
    clickedDownstreamNodeId: PropTypes.string,
    rightClickedNodeId: PropTypes.string,
    hoveredNodeId: PropTypes.string,
    onReset: PropTypes.func.isRequired,
    onClickNode: PropTypes.func.isRequired,
    onClickLink: PropTypes.func.isRequired,
    onRightClickNode: PropTypes.func.isRequired,
    onClearRightClick: PropTypes.func.isRequired,
    onHoverNode: PropTypes.func.isRequired,
    onUnhoverNode: PropTypes.func.isRequired,
    isWorkflowEditable: PropTypes.bool.isRequired,

    // Props provided by connect
    toggleStage: PropTypes.func,
    stageConfigurations: PropTypes.arrayOf(STAGE_CONFIGURATION_FULL_SHAPE.isRequired).isRequired,
    workflow: WORKFLOW_FULL_SHAPE.isRequired,
  };

  _handleClickStageRun(e: React.MouseEvent<SVGGElement>, stageRun: StageRun): void {
    e.stopPropagation();
    this.props.onClickNode(stageRun.id);
  }

  _getWorkflowStage(stageRun: StageRun): WorkflowStage {
    const workflowStageId = _.findKey(this.props.stageIdToStageRuns, (stageRuns) =>
      stageRuns.includes(stageRun)
    );
    return this.props.workflowStages.find((stage) => stage.id === workflowStageId)!;
  }

  _handleClickEditStageRun(stageRun: StageRun): void {
    openConfigureStageRunsModalToEditStageRun(this.props.stageRunGraph, stageRun, {
      workflowId: this.props.workflow.id,
    });
  }

  async _handleClickSetExperimentalCondition(stageRun: StageRun): Promise<void> {
    await runModal(SetExperimentalConditionsModal, {
      stageFlowNode: stageRun.stageFlowNode,
      stageConfiguration: this.props.stageIdToStageConfiguration[this._getWorkflowStage(stageRun).id],
      onRequestSubmit: (experimentalConditionValueIds) => {
        StageRunActions.bulkConfigureStageRuns({
          editSpecifiers: [{stageRunId: stageRun.id, experimentalConditionValueIds}],
        });
      },
    });
  }

  _handleCopySelectedPath(stageRun: StageRun): void {
    openConfigureStageRunsModalToDuplicateBranch(this.props.stageRunGraph, stageRun, {
      workflowId: this.props.workflow.id,
    });
  }

  _handleOpenCreateStageRunModal = (workflowStage: WorkflowStage): void => {
    openConfigureStageRunsModalToCreateStageRun(
      this.props.stageRunGraph,
      workflowStage.stageConfiguration.index,
      {workflowId: this.props.workflow.id}
    );
  };

  _handleBulkCreateStageRuns = (stageConfiguration: StageConfiguration): void => {
    openConfigureStageRunsModalToBulkCreateStageRuns(this.props.stageRunGraph, stageConfiguration.index, {
      workflowId: this.props.workflow.id,
    });
  };

  _handleBulkSplitStageRuns = (workflowStage: WorkflowStage): void => {
    StageRunActions.splitByInputs(workflowStage);
  };

  _renderStageMenu(stageConfiguration: StageConfiguration): JSX.Element | null {
    if (!this.props.workflow.permissions.writable) {
      return null;
    }
    const workflowStage = this.props.workflowStages.find(
      (stage) => stage.stageConfiguration.id === stageConfiguration.id
    )!;
    return (
      <StageMenu
        baseWorkflowTemplateVersionId={this.props.workflow.baseWorkflowTemplateVersionId}
        className="stageVisualization-icon"
        onBulkCreateStageRuns={this._handleBulkCreateStageRuns}
        onBulkSplitStageRuns={this._handleBulkSplitStageRuns}
        onCreate={this._handleOpenCreateStageRunModal}
        stageConfiguration={stageConfiguration}
        workflowStage={workflowStage}
      />
    );
  }

  override render(): JSX.Element {
    const {
      containerWidth,
      stageIdToStageRuns,
      clickedDownstreamNodeId,
      clickedUpstreamNodeId,
      onReset,
      rightClickedNodeId,
      onRightClickNode,
      onClearRightClick,
      hoveredNodeId,
      onClickLink,
      onHoverNode,
      onUnhoverNode,
      stageConfigurations,
    } = this.props;

    const shouldAggregateVisualization = WorkflowUtil.getShouldAggregateVisualization(stageIdToStageRuns);
    // eslint-disable-next-line no-restricted-syntax
    const stageRuns = _.flatten(Object.values(stageIdToStageRuns));

    // TODO(T11775): use prop if not aggregated
    let stageRunGraph: StageRunGraph | AggregatedStageRunGraph;
    let graphLayout: GraphLayout;
    if (shouldAggregateVisualization) {
      stageRunGraph = new AggregatedStageRunGraph(new StageRunGraph(stageRuns, stageConfigurations));
      graphLayout = new GraphLayout(stageRunGraph, AGGREGATED_STAGE_RUN_RADIUS);
    } else {
      stageRunGraph = new StageRunGraph(stageRuns, stageConfigurations);
      graphLayout = new GraphLayout(stageRunGraph, STAGE_RUN_RADIUS);
    }

    const armsHeight = graphLayout.getTreeHeight();
    const totalWidth = graphLayout.getTotalWidth(containerWidth);
    const totalHeight = graphLayout.getTotalHeight(containerWidth);
    const stageRunLinks = graphLayout.getLinks({
      leftBoundary: 0,
      rightBoundary: totalWidth,
    });

    return (
      <div
        className="workflowVisualization"
        style={{width: totalWidth, height: totalHeight}}
        onClick={onReset}
      >
        <div className="workflowVisualization-stages">
          {stageConfigurations.map((stageConfiguration) => (
            <StageVisualization
              key={stageConfiguration.id}
              name={stageConfiguration.stageName}
              height={totalHeight}
              width={graphLayout.getTagWidth(stageConfiguration.index)}
              icon={this._renderStageMenu(stageConfiguration)}
            />
          ))}
        </div>
        <div
          className={classNames('workflowVisualization-arms', {
            'is-tall': graphLayout.isTallVisualization(),
          })}
        >
          <svg width={totalWidth} height={armsHeight}>
            {stageRunLinks.map(
              ({key, leftPosition, rightPosition, parentNodeId, childNodeId, isSecondary}) => {
                // Node to refer to for status
                const referenceNodeId = (childNodeId || parentNodeId)!;
                let stageRunStatus;
                if (shouldAggregateVisualization) {
                  stageRunStatus = (stageRunGraph as AggregatedStageRunGraph).getAggregatedStageRunStatus(
                    referenceNodeId
                  );
                } else {
                  stageRunStatus = (stageRunGraph as StageRunGraph).getStageRun(referenceNodeId).status;
                }
                const StageRunLinkComponent = isSecondary
                  ? StageFlowNodeSecondaryLinkVisualization
                  : StageRunLinkVisualization;
                return (
                  <StageRunLinkComponent
                    key={key}
                    highlightMode={getLinkHighlightMode(parentNodeId, childNodeId, {
                      clickedUpstreamNodeId,
                      clickedDownstreamNodeId,
                      taggedNodeGraph: stageRunGraph,
                    })}
                    isDiscarded={stageRunStatus === STAGE_RUN_STATUS.DISCARDED}
                    onClick={(e) => {
                      e.stopPropagation();
                      onClickLink(parentNodeId, childNodeId);
                    }}
                    leftPosition={leftPosition}
                    rightPosition={rightPosition}
                  />
                );
              }
            )}
            {stageRunGraph.getNodeIds().map((nodeId) => {
              const highlightMode = getNodeHighlightMode(nodeId, {
                clickedUpstreamNodeId,
                clickedDownstreamNodeId,
                taggedNodeGraph: stageRunGraph,
              });
              const stage = this.props.workflowStages.find(
                (workflowStage) => workflowStage.stageConfiguration.index === stageRunGraph.getTag(nodeId)
              );
              assert(Boolean(stage), `No stage found at index ${stageRunGraph.getTag(nodeId)}`);
              if (shouldAggregateVisualization) {
                return (
                  <AggregatedStageRunVisualization
                    key={nodeId}
                    numStageRuns={
                      (stageRunGraph as AggregatedStageRunGraph).getNodeIdsByAggregatedNodeId(nodeId).length
                    }
                    onClick={(e) => {
                      // Prevent click from bubbling up to the background div
                      e.stopPropagation();
                      this.props.toggleStage(stage!.id);
                    }}
                    position={graphLayout.getNodePosition(nodeId)}
                    stageRunStatus={(stageRunGraph as AggregatedStageRunGraph).getAggregatedStageRunStatus(
                      nodeId
                    )}
                  />
                );
              } else {
                const stageWidth = graphLayout.getTagWidth(stageRunGraph.getTag(nodeId));
                const stageRun = (stageRunGraph as StageRunGraph).getStageRun(nodeId);
                const workflowStage = this._getWorkflowStage(stageRun);
                return (
                  <Dropdown
                    key={nodeId}
                    menu={
                      <StageRunMenu
                        stageRun={stageRun}
                        onEditStageRun={() => {
                          this._handleClickEditStageRun(stageRun);
                        }}
                        onOpenSetExperimentalCondition={() => {
                          this._handleClickSetExperimentalCondition(stageRun);
                        }}
                        openSetExperimentalConditionDisabled={
                          !stageConfigurationHasExperimentalConditionValues(
                            this.props.stageIdToStageConfiguration[workflowStage.id]
                          )
                        }
                        onCopySelectedPath={() => {
                          this._handleCopySelectedPath(stageRun);
                        }}
                        isStageEditable={workflowStage.permissions.writable}
                        isWorkflowEditable={this.props.isWorkflowEditable}
                      />
                    }
                    isOpen={nodeId === rightClickedNodeId}
                    onToggle={() => {
                      if (nodeId === rightClickedNodeId) {
                        onClearRightClick();
                      }
                    }}
                  >
                    <StageFlowNodeVisualization
                      highlightMode={highlightMode}
                      experimentalConditionValues={stageRun.stageFlowNode.experimentalConditionValues}
                      isNodeClickable={true}
                      isHovered={nodeId === hoveredNodeId}
                      onClick={(e) => {
                        this._handleClickStageRun(e, stageRun);
                      }}
                      onHover={() => {
                        onHoverNode(nodeId);
                      }}
                      onUnhover={() => {
                        onUnhoverNode(nodeId);
                      }}
                      onRightClick={(e) => {
                        e.preventDefault();
                        if (highlightMode !== HighlightMode.HIGHLIGHTED) {
                          this.props.onClickNode(nodeId);
                        }
                        onRightClickNode(nodeId);
                      }}
                      position={graphLayout.getNodePosition(nodeId)}
                      stageWidth={stageWidth}
                      relativeX={graphLayout.getRelativeX(nodeId)}
                      stageRunStatus={stageRun.status}
                      shouldShowExperimentalCondition={stageRunGraph.isLastDescendantWithTag(nodeId)}
                      tooltipTitle={stageRun.name}
                    />
                  </Dropdown>
                );
              }
            })}
          </svg>
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowVisualization);
